import { TemplateCategories } from './types';

// Cache for loaded templates to avoid repeated imports
const templateCache = new Map<string, TemplateCategories>();

// Available languages - all 11 supported languages
const availableLanguages = ['en', 'zh', 'es', 'fr', 'id', 'hi', 'ja', 'vi', 'ko', 'de', 'it'];

/**
 * Get the appropriate language code for template loading
 * Falls back to English if the requested language isn't available
 */
function getTemplateLanguage(requestedLanguage: string): string {
  // Extract language code (e.g., 'en-US' -> 'en')
  const langCode = requestedLanguage.split('-')[0];
  
  // Return the language if available, otherwise fallback to English
  return availableLanguages.includes(langCode) ? langCode : 'en';
}

/**
 * Static template imports - all languages loaded at build time
 */
const templateModules = {
  en: {
    creative: () => import('./en/creative'),
    professional: () => import('./en/professional'),
    educational: () => import('./en/educational'),
    dialogue: () => import('./en/dialogue')
  },
  zh: {
    creative: () => import('./zh/creative'),
    professional: () => import('./zh/professional'),
    educational: () => import('./zh/educational'),
    dialogue: () => import('./zh/dialogue')
  },
  es: {
    creative: () => import('./es/creative'),
    professional: () => import('./es/professional'),
    educational: () => import('./es/educational'),
    dialogue: () => import('./es/dialogue')
  },
  fr: {
    creative: () => import('./fr/creative'),
    professional: () => import('./fr/professional'),
    educational: () => import('./fr/educational'),
    dialogue: () => import('./fr/dialogue')
  },
  id: {
    creative: () => import('./id/creative'),
    professional: () => import('./id/professional'),
    educational: () => import('./id/educational'),
    dialogue: () => import('./id/dialogue')
  },
  hi: {
    creative: () => import('./hi/creative'),
    professional: () => import('./hi/professional'),
    educational: () => import('./hi/educational'),
    dialogue: () => import('./hi/dialogue')
  },
  ja: {
    creative: () => import('./ja/creative'),
    professional: () => import('./ja/professional'),
    educational: () => import('./ja/educational'),
    dialogue: () => import('./ja/dialogue')
  },
  vi: {
    creative: () => import('./vi/creative'),
    professional: () => import('./vi/professional'),
    educational: () => import('./vi/educational'),
    dialogue: () => import('./vi/dialogue')
  },
  ko: {
    creative: () => import('./ko/creative'),
    professional: () => import('./ko/professional'),
    educational: () => import('./ko/educational'),
    dialogue: () => import('./ko/dialogue')
  },
  de: {
    creative: () => import('./de/creative'),
    professional: () => import('./de/professional'),
    educational: () => import('./de/educational'),
    dialogue: () => import('./de/dialogue')
  },
  it: {
    creative: () => import('./it/creative'),
    professional: () => import('./it/professional'),
    educational: () => import('./it/educational'),
    dialogue: () => import('./it/dialogue')
  }
};

/**
 * Load templates for a specific language using static imports
 */
async function loadTemplatesForLanguage(language: string): Promise<TemplateCategories> {
  const langCode = getTemplateLanguage(language);
  
  // Check cache first
  if (templateCache.has(langCode)) {
    return templateCache.get(langCode)!;
  }

  try {
    // Get the template modules for this language
    const modules = templateModules[langCode as keyof typeof templateModules];
    if (!modules) {
      throw new Error(`No templates found for language: ${langCode}`);
    }

    // Load all categories
    const [creativeModule, professionalModule, educationalModule, dialogueModule] = await Promise.all([
      modules.creative(),
      modules.professional(),
      modules.educational(),
      modules.dialogue()
    ]);

    const templates: TemplateCategories = {
      creative: creativeModule.creative,
      professional: professionalModule.professional,
      educational: educationalModule.educational,
      dialogue: dialogueModule.dialogue
    };

    // Cache the loaded templates
    templateCache.set(langCode, templates);
    
    return templates;
  } catch {
//    console.warn(`Failed to load templates for language ${langCode}:`, error);
    
    // If not English and failed, try English as fallback
    if (langCode !== 'en') {
//      console.warn(`Falling back to English templates`);
      return loadTemplatesForLanguage('en');
    }
    
    // If English also fails, throw error
    throw new Error('Failed to load templates');
  }
}

/**
 * Get templates for the current language
 * This is the main function that components should use
 */
export async function getTemplates(language: string = 'en'): Promise<TemplateCategories> {
  return loadTemplatesForLanguage(language);
}

/**
 * Get cached templates synchronously if available
 * Returns null if templates need to be loaded
 */
export function getCachedTemplates(language: string = 'en'): TemplateCategories | null {
  const langCode = getTemplateLanguage(language);
  return templateCache.get(langCode) || null;
}

/**
 * Preload templates for a language
 * Useful for warming up the cache
 */
export async function preloadTemplates(language: string = 'en'): Promise<void> {
  try {
    await loadTemplatesForLanguage(language);
  } catch {
//    console.warn(`Failed to preload templates for ${language}:`, error);
  }
}

/**
 * Clear template cache (useful for development/testing)
 */
export function clearTemplateCache(): void {
  templateCache.clear();
}

// Export types for convenience
export type { TemplateCategories, PromptTemplate, PromptTemplateCategory } from './types';
